/**
 * Copyright (c) 2020
 *
 * Contains the representation of a Customer
 *
 * @summary customer class definition
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2020-06-08 10:21:56
 * Last modified  : 2021-08-17 16:17:44
 */

import BillingPortal from '@/models/customersManager/entity/BillingPortal.js'
import CustomerSubscription from '@/models/customersManager/entity/CustomerSubscription.js'

class Customer {

    /**
     * @type id Integer
     */
    #id

    /**
     * @type chargifyId Integer
     */
    #chargifyId

    /**
     * @type email String
     */
    #email

    /**
     * @type firstName String
     */
    #firstName

    /**
     * @type lastName String
     */
    #lastName

    /**
     * @type organization null|String
     */
    #organization

    /**
     * @type createdAt Date
     */
    #createdAt

    /**
     * @type updatedAt Date
     */
    #updatedAt

    /**
     * @type roles Array
     */
    #roles = []

    /**
     * @type billingPortal null|BillingPortal
     */
    #billingPortal = null

    /**
     * @type customerSubscriptions CustomerSubscription[]
     */
    #customerSubscriptions = []

    /**
     * @param rawCustomer Object
     */
    constructor(rawCustomer = null) {
        if (rawCustomer !== null) {
            const {
                id,
                chargifyId,
                email,
                firstName,
                lastName,
                organization,
                createdAt,
                updatedAt,
                roles,
                billingPortal,
                customerSubscriptions
            } = rawCustomer
            this.id = id
            this.chargifyId = chargifyId
            this.email = email
            this.firstName = firstName
            this.lastName = lastName
            this.organization = organization
            this.createdAt = createdAt
            this.updatedAt = updatedAt
            this.roles = roles
            this.billingPortal = billingPortal
            this.customerSubscriptions = customerSubscriptions
        }
    }

    /**
     * @returns Integer
     */
    get id () {
        return this.#id
    }

    /**
     * @param id String
     * @returns void
     */
    set id (id) {
        this.#id = parseInt(id)
    }

    /**
     * @returns Integer
     */
    get chargifyId () {
        return this.#chargifyId
    }

    /**
     * @param chargifyId String
     * @returns void
     */
    set chargifyId (chargifyId) {
        this.#chargifyId = parseInt(chargifyId)
    }

    /**
     * @returns String
     */
    get email () {
        return this.#email
    }

    /**
     * @param email String
     * @returns void
     */
    set email (email) {
        this.#email = email
    }

    /**
     * @returns String
     */
    get firstName () {
        return this.#firstName
    }

    /**
     * @param firstName String
     * @returns void
     */
    set firstName (firstName) {
        this.#firstName = firstName
    }

    /**
     * @returns String
     */
    get lastName () {
        return this.#lastName
    }

    /**
     * @param lastName String
     * @returns void
     */
    set lastName (lastName) {
        this.#lastName = lastName
    }

    /**
     * @returns null|String
     */
    get organization () {
        return this.#organization
    }

    /**
     * @param organization String
     * @returns void
     */
    set organization (organization) {
        this.#organization = organization
    }

    /**
     * @returns Date
     */
    get createdAt () {
        return this.#createdAt
    }

    /**
     * @param createdAt String
     * @returns void
     */
    set createdAt (createdAt) {
        this.#createdAt = new Date(createdAt)
    }

    /**
     * @returns Date
     */
    get updatedAt () {
        return this.#updatedAt
    }

    /**
     * @param updatedAt String
     * @returns void
     */
    set updatedAt (updatedAt) {
        this.#updatedAt = new Date(updatedAt)
    }

    /**
     * @returns Array
     */
    get roles () {
        return this.#roles
    }

    /**
     * @param roles Array
     * @returns void
     */
    set roles (roles) {
        this.#roles = []
        if (roles !== null && typeof roles !== 'undefined') {
            this.#roles = roles
        }
    }

    /**
     * @returns Boolean
     */
    get isAdmin () {
        return typeof this.roles.find(role => role === 'ROLE_ADMIN') !== 'undefined'
    }

    /**
     * @returns Boolean
     */
    get isAllowedToSwitch () {
        return typeof this.roles.find(role => role === 'ROLE_ALLOWED_TO_SWITCH') !== 'undefined'
    }

    /**
     * @returns BillingPortal|null
     */
    get billingPortal () {
        return this.#billingPortal
    }

    /**
     * @param rawBillingPortal null|Object
     * @returns void
     */
    set billingPortal (rawBillingPortal) {
        this.#billingPortal = null
        if (rawBillingPortal !== null && typeof rawBillingPortal !== 'undefined') {
            this.#billingPortal = new BillingPortal(rawBillingPortal)
        }
    }

    /**
     * @returns CustomerSubscription[]|null
     */
    get customerSubscriptions () {
        return this.#customerSubscriptions
    }

    /**
     * @param rawCustomerSubscriptions null|Object[]
     * @returns void
     */
    set customerSubscriptions (rawCustomerSubscriptions) {
        this.#customerSubscriptions = []
        if (rawCustomerSubscriptions !== null && typeof rawCustomerSubscriptions !== 'undefined') {
            rawCustomerSubscriptions.forEach((rawCustomerSubscription) => {
                const customerSubscription = new CustomerSubscription(rawCustomerSubscription)
                this.#customerSubscriptions = [...this.#customerSubscriptions, customerSubscription]
            });
        }
    }




}

export default Customer
